import React from "react";

import {
  PAGINATION_STATUS_SELECTED,
  PAGINATION_STATUS_DISABLED,
  PAGINATION_STATUS_CLICKABLE,
} from "../../../../constants";

/**
 * @typedef {import("../../../../jsdoc/PaginationPage.js").PaginationPage} PaginationPage
 */

/**
 * Generates list of PaginationPage objects corresponding to the given page information
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {number} [spread=2] number of pages shown before and after current page
 * @returns {PaginationPage[]}
 */
const generatePageList = (currentPage, totalPages, spread = 2) => {
  const pageList = [];

  pageList.push({
    display: "First",
    number: 0,
    status: currentPage === 0 ? PAGINATION_STATUS_DISABLED : PAGINATION_STATUS_CLICKABLE,
  });
  pageList.push({
    display: "Previous",
    number: currentPage - 1,
    status: currentPage === 0 ? PAGINATION_STATUS_DISABLED : PAGINATION_STATUS_CLICKABLE,
  });

  for (let i = spread; i > 0; i--) {
    if (currentPage - i >= 0) {
      pageList.push({
        display: (currentPage - i + 1).toString(),
        number: currentPage - i,
        status: PAGINATION_STATUS_CLICKABLE,
      });
    }
  }

  pageList.push({ display: (currentPage + 1).toString(), number: currentPage, status: PAGINATION_STATUS_SELECTED });

  let i = currentPage + 1;
  while (i <= currentPage + spread && i < totalPages) {
    pageList.push({
      display: (i + 1).toString(),
      number: i,
      status: PAGINATION_STATUS_CLICKABLE,
    });
    i++;
  }

  pageList.push({
    display: "Next",
    number: currentPage + 1,
    status: currentPage === totalPages - 1 ? PAGINATION_STATUS_DISABLED : PAGINATION_STATUS_CLICKABLE,
  });
  pageList.push({
    display: "Last",
    number: totalPages - 1,
    status: currentPage === totalPages - 1 ? PAGINATION_STATUS_DISABLED : PAGINATION_STATUS_CLICKABLE,
  });

  return pageList;
};

/**
 * Generic PaginationBar Component
 * @param {Object} props
 * @param {string} [props.id=""]
 * @param {string} [props.className=""]
 * @param {number} props.currentPage
 * @param {function} props.setCurrentPage
 * @param {number} props.totalPages
 * @returns {React.JSX.Element}
 */
export default function PaginationBar({ id = "", className = "", currentPage, setCurrentPage, totalPages }) {
  return (
    <div id={id} className={className}>
      <ul className="pagination">
        {generatePageList(currentPage, totalPages).map(({ display, number, status }) => {
          const onClick = () => {
            if (status === PAGINATION_STATUS_CLICKABLE) {
              setCurrentPage(number);
              window.scrollTo(0, 0);
            }
          };
          return (
            <li key={display} className={status} onClick={onClick}>
              <a href="javascript:;">{display}</a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
